import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import appConfig from "../../appConfig";
import { getToken } from "../../services/apiService";

// Same text the API answers when an upload uses Agile without a session (UploadsController.AgileSignInRequired)
export const AGILE_SIGN_IN_REQUIRED = "Sign in to Agile to run rows that use Agile.";

const NOT_SIGNED_IN = { signedIn: false, agileUser: null };

// Agile session of the signed-in user. The API keeps it in memory and checks with Agile that it still works; the password is not stored.
export const useAgileSession = () => {
  const { instance, accounts } = useMsal();
  const [session, setSession] = useState(NOT_SIGNED_IN);

  const call = useCallback(
    async (method, data) => {
      const accessToken = await getToken(instance, accounts);
      if (!accessToken) throw new Error("There was an authentication error. Please sign in again.");
      const response = await axios({
        method,
        url: `${appConfig.apiUrl}agile/session`,
        data,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.data?.status !== "OK") throw new Error(response.data?.message || "Agile sign-in failed.");
      const next = response.data.data || NOT_SIGNED_IN;
      setSession(next);
      return next;
    },
    [instance, accounts]
  );

  const refresh = useCallback(() => call("get").catch(() => NOT_SIGNED_IN), [call]);
  const signIn = useCallback((user, password) => call("post", { user, password }), [call]);
  const signOut = useCallback(() => call("delete").catch(() => setSession(NOT_SIGNED_IN)), [call]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { session, refresh, signIn, signOut };
};

// Eye of the show / hide password button, crossed while the password is shown
const EyeIcon = ({ crossed }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    {crossed && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

// "Sign in to AGILE" dialog, like Flowchart Checker
export function AgileSignInModal({ show, onHide, onSignedIn, signIn }) {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!show) {
      setPassword("");
      setShowPassword(false);
      setError("");
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const session = await signIn(user.trim(), password);
      setPassword("");
      onSignedIn?.(session);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Sign in to AGILE</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-start">
          <p className="text-muted small">
            Agile BOMs are read with your own Agile account. Your password is only used to sign in and is never
            stored. Agile ends the session after a while without use; you will be asked to sign in again then.
          </p>
          {error && <div className="alert alert-danger upload-alert py-2">{error}</div>}
          <div className="mb-3">
            <label className="form-label upload-label" htmlFor="agileUser">User</label>
            <input
              id="agileUser"
              className="form-control"
              autoComplete="username"
              autoFocus
              value={user}
              onChange={(e) => setUser(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="form-label upload-label" htmlFor="agilePassword">Password</label>
            <div className="input-group">
              <input
                id="agilePassword"
                type={showPassword ? "text" : "password"}
                className="form-control"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword((shown) => !shown)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                title={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon crossed={showPassword} />
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !user.trim() || !password}>
            {busy ? "Signing in..." : "Sign in"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

// "Signed in to AGILE as X · Change credentials" / "Not signed in to AGILE · Sign in"
export function AgileSessionStatus({ session, onSignIn, onSignOut }) {
  return (
    <div className="small text-muted d-flex align-items-center gap-1 flex-wrap">
      {session.signedIn ? (
        <>
          <span>Signed in to AGILE as <b>{session.agileUser}</b></span>
          <span>·</span>
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={onSignOut}>
            Change credentials
          </button>
        </>
      ) : (
        <>
          <span>Not signed in to AGILE</span>
          <span>·</span>
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={onSignIn}>
            Sign in
          </button>
        </>
      )}
    </div>
  );
}
